import {Injectable} from '@angular/core';
import {ILanguage} from './ILanguage';
import {HttpClient} from '@angular/common/http';
import {NavigationEnd, Router} from '@angular/router';
import {BaseHttpService} from '../base-http/base-http.service';
import {filter, first} from 'rxjs/operators';
import {IArticle} from '../base-http/IArticle';
import {TranslatableString} from '../../models/TranslatableString';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  servicesHeader: TranslatableString;
  servicesArticle: TranslatableString;
  bulgariaHeader: TranslatableString;
  bulgariaArticle: TranslatableString;
  varnaHeader: TranslatableString;
  varnaArticle: TranslatableString;
  historyHeader: TranslatableString;
  historyArticle: TranslatableString;
  policyHeader: string;
  policyArticle: string;
  regions: { header: TranslatableString, article: TranslatableString }[];
  howToArticles: { header: TranslatableString, article: TranslatableString }[];
  principleHeader: TranslatableString;
  principleArticle: TranslatableString;
  currentLanguage: ILanguage;
  private languageSource = new BehaviorSubject('bg');
  currentLanguageKey = this.languageSource.asObservable();
  available: boolean;
  private initialLanguage: string;
  private languages: { bg: ILanguage; en: ILanguage; ru: ILanguage };

  constructor(
    private http: HttpClient,
    private router: Router,
    private baseHttp: BaseHttpService,) {
    this.languages = {bg: null, en: null, ru: null};
    this.getLanguage('bg').subscribe((bg: ILanguage) => {
      this.languages.bg = bg;
    });
    this.getLanguage('en').subscribe((en: ILanguage) => {
      this.languages.en = en;
    });
    this.getLanguage('ru').subscribe((ru: ILanguage) => {
      this.languages.ru = ru;
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(res => {
        this.initialLanguage = (res as NavigationEnd).urlAfterRedirects.substring(1, 3);
        const interval = setInterval(() => {
          if (this.languages[this.initialLanguage]) {
            clearInterval(interval);
            this.setLanguage(this.initialLanguage);
            this.available = true;
            this.getCompanyServices();
            this.getVarna();
            this.getBulgaria();
            this.getRegions();
            this.getHowTo();
            this.getHistory();
            this.getPrinciples();
            this.getPolicy();
          }
        }, 100);
      });
  }

  translate(key: string) {
    return this.currentLanguage[key];
  }

  setLanguage(key: string) {
    this.languageSource.next(key);
    this.navigateTo(key);
  }

  getCompanyServices() {
    if (this.servicesArticle && this.servicesHeader) {
      return;
    }
    this.baseHttp.getArticle('companyServices').subscribe((value: IArticle) => {
      this.servicesHeader = new TranslatableString(value[0].value, value[0].header_en, value[0].header_bg, value[0].header_ru);
      this.servicesArticle = new TranslatableString(value[0].value, value[0].article_en, value[0].article_bg, value[0].article_ru);
    });
  }

  getBulgaria() {
    if (this.bulgariaArticle && this.bulgariaHeader) {
      return;
    }
    this.baseHttp.getArticle('country').subscribe((value: IArticle) => {
      this.bulgariaHeader = new TranslatableString(value[0].value, value[0].header_en, value[0].header_bg, value[0].header_ru);
      this.bulgariaArticle = new TranslatableString(value[0].value, value[0].article_en, value[0].article_bg, value[0].article_ru);
    });
  }

  getVarna() {
    if (this.varnaArticle && this.varnaHeader) {
      return;
    }
    this.baseHttp.getArticle('city').subscribe((value: IArticle) => {
      this.varnaHeader = new TranslatableString(value[0].value, value[0].header_en, value[0].header_bg, value[0].header_ru);
      this.varnaArticle = new TranslatableString(value[0].value, value[0].article_en, value[0].article_bg, value[0].article_ru);
    });
  }

  getRegions() {
    if (this.regions) {
      return;
    }
    this.regions = [];
    this.baseHttp.getArticle('region').subscribe((val: Array<IArticle>) => {
      val.forEach((region: IArticle) => {
        const regionsHeader = new TranslatableString(
          region.header,
          region.header_en,
          region.header_bg,
          region.header_ru);
        const regionArticle = new TranslatableString(
          region.article,
          region.article_en,
          region.article_bg,
          region.article_ru
        );
        this.regions.push({header: regionsHeader, article: regionArticle});
      });
    });
  }

  getHowTo() {
    if (this.howToArticles) {
      return;
    }
    this.howToArticles = [];
    this.baseHttp.getArticle('howTo').subscribe((val: Array<IArticle>) => {
      val.forEach((howToArticle: IArticle) => {
        const articleHeader = new TranslatableString(
          howToArticle.header,
          howToArticle.header_en,
          howToArticle.header_bg,
          howToArticle.header_ru);
        const articleArticle = new TranslatableString(
          howToArticle.article,
          howToArticle.article_en,
          howToArticle.article_bg,
          howToArticle.article_ru);
        this.howToArticles.push({header: articleHeader, article: articleArticle});
      });
    });
  }

  getHistory() {
    if (this.historyArticle && this.historyHeader) {
      return;
    }
    this.baseHttp.getArticle('companyHistory').subscribe((val: IArticle) => {
      this.historyHeader = new TranslatableString(val[0].header, val[0].header_en, val[0].header_bg, val[0].header_ru);
      this.historyArticle = new TranslatableString(val[0].article.split('&nbsp;').join(' '),
        val[0].article_en.split('&nbsp;').join(' '), val[0].article_bg.split('&nbsp;').join(' '),
        val[0].article_ru.split('&nbsp;').join(' '));
    });
  }

  getPrinciples() {
    if (this.principleArticle && this.principleHeader) {
      return;
    }
    this.baseHttp.getArticle('companyPrinciples').subscribe((value: IArticle) => {
      this.principleHeader = new TranslatableString(value[0].value, value[0].header_en, value[0].header_bg, value[0].header_ru);
      this.principleArticle = new TranslatableString(value[0].value, value[0].article_en, value[0].article_bg, value[0].article_ru);
    });
  }

  getPolicy() {
    if (this.policyHeader && this.policyArticle) {
      return;
    }
    this.http.get<any>('./assets/policy.json').subscribe((val) => {
      this.policyHeader = val.header;
      this.policyArticle = val.article;
    });
  }

  private getLanguage(key: string) {
    return this.http.get<ILanguage>('./assets/i18n/' + key + '.json');
  }

  private navigateTo(key: string) {
    const valueToReplace = this.router.url.substring(1, 3);
    this.router.navigateByUrl(this.router.url.replace(valueToReplace, key));
  }
}
